import { Text } from '@/components/text';
import { Box } from '@/components/box';
import { ToolkitGridItem } from '../toolkit-grid-item';
import { ProjectPhaseWithToolkitItems } from '@/components/project/types';
import { ScrollProgressIndicator } from '@/components/scroll-progress-indicator';
import { useRef } from 'react';
import { ImageCarousel } from '@/components/work/work-section/components/image-carousel';

type ImagePosition = 'top-left-sm' | 'bottom-left-lg' | 'top-right-sm';

interface ToolkitItemsGridProps {
  projectPhase: ProjectPhaseWithToolkitItems;
  imagePosition: ImagePosition;
}

const IMAGE_POSITION_TO_GRID_STYLING: Record<
  ImagePosition,
  { gridColumn: string; gridRow: string }
> = {
  'top-left-sm': {
    gridColumn: '1 / 3',
    gridRow: '1 / 3',
  },
  'bottom-left-lg': {
    gridColumn: '1 / 4',
    gridRow: '2 / 4',
  },
  'top-right-sm': {
    gridColumn: '-1 / -3',
    gridRow: '1 / 3',
  },
};

export function ToolkitItemsGrid({
  projectPhase,
  imagePosition,
}: ToolkitItemsGridProps) {
  const scrollableContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <>
      <Box
        // The ID is used to scroll to this section in `ProjectPhasesDiagram`
        id={projectPhase._id}
        css={{
          gridColumn: 'main',
          display: 'flex',
          flexFlow: 'column nowrap',
          '@bp5': {
            gridColumn: '3 / 6',
          },
        }}
      >
        <Text
          size={13}
          css={{
            margin: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {projectPhase.order}
        </Text>
        <Text size={6} css={{ mb: '$4' }}>
          {projectPhase.title}
        </Text>
        <Text size={2} css={{ mb: '$7' }}>
          {projectPhase.description}
        </Text>
      </Box>

      <ScrollProgressIndicator
        scrollableContainerRef={scrollableContainerRef}
        css={{ mb: '$3', '@bp5': { display: 'none' } }}
      />

      <Box
        ref={scrollableContainerRef}
        css={{
          $$cardWidth: '12rem',
          $$cardHeight: '18rem',
          gridColumn: 'main',
          gridColumnEnd: '-1',
          mb: '$12',
          height: 'calc($$cardHeight * 2 + 1rem)',
          padding: '1px',
          overflow: 'scroll hidden',
          '@bp5': {
            overflow: 'auto',
            mb: '$13',
            gridColumn: '7 / 18',
            height: 'auto',
            $$cardHeight: '20rem',
          },
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexFlow: 'column wrap',
            justifyContent: 'start',
            alignItems: 'start',
            background: 'rbga(255,0,0,0.5)',
            width: 'min-content',
            height: '100%',
            '@bp5': {
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gridColumn: '6 / 17',
              width: 'auto',
              height: 'auto',
            },
          }}
        >
          <Box
            css={{
              alignSelf: 'stretch',
              position: 'relative',
              width: '$$cardWidth',
              height: '$$cardHeight',
              gridColumn:
                IMAGE_POSITION_TO_GRID_STYLING[imagePosition].gridColumn,
              gridRow: IMAGE_POSITION_TO_GRID_STYLING[imagePosition].gridRow,
              '@bp5': {
                width: 'auto',
                height: 'auto',
              },
            }}
          >
            <Box
              css={{
                position: 'absolute',
                top: -0.5,
                left: -0.5,
                width: 'calc(100% + 1px)',
                height: 'calc(100% + 1px)',
                border: '1px rgb(150,150,150) solid',
              }}
            >
              <ImageCarousel images={projectPhase.images} canAnimate />
            </Box>
          </Box>
          {projectPhase.toolkit.map((toolkitItem) => (
            <ToolkitGridItem
              key={toolkitItem._id}
              toolkitItem={toolkitItem}
              css={{
                width: '$$cardWidth',
                height: '$$cardHeight',
                '@bp5': {
                  width: 'auto',
                },
              }}
            />
          ))}

          {/*
            Hacky way of adding some padding to the right of the scrollable container.
            A regular padding-right on the scrollable container didn't work. The issue
            might be browser quirks with their implementation of wrapping columns.

            Instead, we add 1 or 2 invisible divs (depending on whether there are an
            even or odd number of items in the container) to add this padding.
          */}
          {new Array(projectPhase.toolkit.length % 2 ? 1 : 2)
            .fill(null)
            .map((_, index) => (
              <Box
                key={index}
                css={{
                  width: '4rem',
                  height: '$$cardHeight',
                  '@bp5': { display: 'none' },
                }}
              />
            ))}
        </Box>
      </Box>
    </>
  );
}
